import { classifyAccessChange } from "@/shared/access/store-access";
import type { QueryClient } from "@tanstack/react-query";
import { createSessionChannel } from "@a2z/browser-session";
import { fetchSessionAfterBindingFailure } from "@/lib/api/client";
import { finishPrivateSessionChange, getPrivateSessionState, invalidatePrivateSession } from "@/lib/api/private-session";
import { signOutSellerFirebaseAuth } from "@/lib/firebase/auth-session";
import { queryKeys } from "@/lib/query/core/query-keys";
import type { SellerAuthSessionResponse } from "@/lib/api/types";
export const sessionChannel = createSessionChannel("seller");
export async function resetPrivateState(client: QueryClient, generation = getPrivateSessionState().generation) {
  if (generation !== getPrivateSessionState().generation) return;
  await client.cancelQueries({ queryKey: ["seller"] });
  if (generation !== getPrivateSessionState().generation) return;
  client.getMutationCache().clear();
  client.removeQueries({ queryKey: ["seller"] });
}
export async function inspectPrivateBinding() {
  const response = await fetchSessionAfterBindingFailure();
  if (response.status === 401 || response.status === 403) return undefined;
  if (!response.ok) throw new Error("Session inspection failed");
    const payload = await response.json() as SellerAuthSessionResponse;
    if (!payload.seller?.storeSlug || !payload.seller.id) throw new Error("Invalid session inspection");
    return payload;
}
export async function recoverPrivateBinding(client: QueryClient, generation: number) {
  const payload = await inspectPrivateBinding();
  if (generation !== getPrivateSessionState().generation) throw new DOMException("Session changed", "AbortError");
  await resetPrivateState(client, generation);
  if (generation !== getPrivateSessionState().generation) throw new DOMException("Session changed", "AbortError");
  const storeSlug = payload ? payload.seller?.storeSlug : undefined;
  finishPrivateSessionChange(storeSlug, payload?.seller.id);
    if (payload) client.setQueryData(queryKeys.seller.session(storeSlug!), payload);
}
export function installSessionChannel(client: QueryClient) {
  sessionChannel.install(() => {
    invalidatePrivateSession();
    void resetPrivateState(client);
    void signOutSellerFirebaseAuth().catch(() => undefined);
  }, () => recoverPrivateBinding(client, getPrivateSessionState().generation));
}

const notSession = ({ queryKey }: { queryKey: readonly unknown[] }) => queryKey[2] !== "session";

/**
 * After an in-place access change, data fetched under the old capabilities is
 * stale: drop what nothing is showing and MARK what is on screen. The session
 * query itself is excluded — its new payload is what triggered this.
 *
 * Marking is deliberately not refetching. At this moment the on-screen queries
 * still carry the options of the render that used the OLD access: a Premium-only
 * query (`enabled: can("categories")`) is still enabled, and refetching it now
 * is a guaranteed 403 STORE_CAPABILITY_REQUIRED that, before 2026-10-07, took
 * the open product page down to "حدث خطأ". The refetch happens in
 * `refetchAccessScopedQueries`, which `StoreAccessBoundary` runs after React
 * has committed the new access — by then every gated query has stood down.
 */
export function refreshAccessScopedQueries(client: QueryClient) {
  client.removeQueries({ queryKey: ["seller"], predicate: (query) => notSession(query) && query.getObserversCount() === 0 });
  void client.invalidateQueries({ queryKey: ["seller"], predicate: notSession, refetchType: "none" });
}

/**
 * The second half of `refreshAccessScopedQueries`, run from a React effect
 * after the new access has committed: invalidate the private queries again and
 * refetch the ones still enabled under the access now on screen. Invalidating
 * here too (not only refetching what was marked) keeps it correct whichever of
 * the two runs first. Not awaited: a refetch answered with a typed 403
 * re-enters `reinspectPrivateAccess`, which must not wait on itself.
 */
export function refetchAccessScopedQueries(client: QueryClient) {
  void client.invalidateQueries({ queryKey: ["seller"], predicate: notSession });
}

/**
 * Access-revision and window-focus `/me` reads land here. Only a change of
 * actor, store or admission (`rebind`) discards private state and bumps the
 * session generation, which remounts the portal (`QueryProvider` keys on it).
 * A capability/permission change (`in-place`) keeps the tree mounted — unsaved
 * forms survive — and refreshes the data instead; a revision-only change does
 * nothing at all. The route gate re-evaluates on the new payload either way.
 */
export async function acceptInspectedSellerSession(client: QueryClient, payload: SellerAuthSessionResponse, generation: number) {
  const previous = getPrivateSessionState();
  if (previous.generation !== generation || previous.changing) throw new DOMException("Session changed", "AbortError");
  const cached = previous.storeSlug ? client.getQueryData<SellerAuthSessionResponse>(queryKeys.seller.session(previous.storeSlug)) : undefined;
  const change = cached ? classifyAccessChange(cached.seller, payload.seller) : "none";
  if (change === "rebind" || (previous.storeSlug && previous.storeSlug !== payload.seller.storeSlug) ||
      (previous.actorId && previous.actorId !== payload.seller.id)) {
    invalidatePrivateSession();
    const next = getPrivateSessionState().generation;
    await resetPrivateState(client, next);
    if (getPrivateSessionState().generation !== next) throw new DOMException("Session changed", "AbortError");
    await signOutSellerFirebaseAuth().catch(() => undefined);
    if (getPrivateSessionState().generation !== next) throw new DOMException("Session changed", "AbortError");
    finishPrivateSessionChange(payload.seller.storeSlug, payload.seller.id);
    client.setQueryData(queryKeys.seller.session(payload.seller.storeSlug), payload);
    return;
  }
  // Deferred past this query function, so the new session payload is cached
  // first. Only marks; the refetch waits for the new access to render.
  if (change === "in-place") setTimeout(() => refreshAccessScopedQueries(client), 0);
}
